import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ArtisanCard from '../components/ArtisanCard';
import SEO from '../components/SEO';

const Artisans = () => {
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    region: '',
    specialite: '',
    recherche: ''
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalArtisans, setTotalArtisans] = useState(0);

  const regions = [
    'Dakar', 'Thiès', 'Saint-Louis', 'Kaolack', 'Ziguinchor',
    'Tambacounda', 'Kolda', 'Fatick', 'Kaffrine', 'Kédougou',
    'Louga', 'Matam', 'Sédhiou', 'Diourbel'
  ];

  const specialites = [
    'Textile et Tissage',
    'Poterie et Céramique',
    'Sculpture sur Bois',
    'Maroquinerie',
    'Bijouterie Traditionnelle',
    'Vannerie',
    'Ferronnerie',
    'Peinture et Art',
    'Instruments de Musique',
    'Décoration'
  ];

  useEffect(() => {
    fetchArtisans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.region, filters.specialite, filters.recherche]);

  const fetchArtisans = async () => {
    setLoading(true);
    try {
      // Créer les paramètres en ne gardant que ceux qui ont une valeur
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`/artisans?${params.toString()}`);
      
      // Vérifier et nettoyer les doublons si nécessaire
      const artisansData = response.data.artisans || [];
      const uniqueArtisans = artisansData.filter((artisan, index, self) =>
        index === self.findIndex((a) => a._id === artisan._id)
      );
      
      console.log('Artisans reçus:', artisansData.length, 'Artisans uniques:', uniqueArtisans.length);
      
      setArtisans(uniqueArtisans);
      setTotalPages(response.data.totalPages || 0);
      setTotalArtisans(response.data.totalArtisans || 0);
    } catch (error) {
      console.error('Erreur lors du chargement des artisans:', error);
      setArtisans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchArtisans();
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO 
        title="Artisans Sénégalais Certifiés | Village Artisanal FIDAK Soumbédioune | Dakar"
        description="Rencontrez les artisans sénégalais du Village Artisanal de Soumbédioune. Potiers, vanniers, bijoutiers, maroquiniers certifiés et soutenus par le Ministère de l'Artisanat du Sénégal. Savoir-faire traditionnel, commerce équitable à Dakar."
        keywords="artisans sénégalais, artisans dakar, artisans FIDAK, artisans soumbédioune, potiers sénégal, vanniers traditionnels, bijoutiers africains, maroquiniers dakar, artisans certifiés sénégal, commerce équitable artisans"
        type="website"
      />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold text-gray-900 dark:text-gray-100 mb-4">
            Artisans Sénégalais Certifiés
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Découvrez {totalArtisans} artisans talentueux du Village Artisanal de Soumbédioune qui perpétuent les traditions du Sénégal
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Rechercher un artisan, une spécialité..."
                  value={filters.recherche}
                  onChange={(e) => setFilters(prev => ({ ...prev, recherche: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-2 bg-primary-800 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
              >
                <i className="fas fa-search"></i>
              </button>
            </div>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Région</label>
              <select
                value={filters.region}
                onChange={(e) => handleFilterChange('region', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Toutes les régions</option>
                {regions.map((region) => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Spécialité</label>
              <select
                value={filters.specialite}
                onChange={(e) => handleFilterChange('specialite', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Toutes les spécialités</option>
                {specialites.map((specialite) => (
                  <option key={specialite} value={specialite}>{specialite}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  page: 1,
                  limit: 12,
                  region: '',
                  specialite: '',
                  recherche: ''
                })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <i className="fas fa-times mr-2"></i>
                Effacer les filtres
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {totalArtisans} artisan{totalArtisans > 1 ? 's' : ''} trouvé{totalArtisans > 1 ? 's' : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <i className="fas fa-spinner fa-spin text-4xl text-primary-800 dark:text-primary-400 mb-4"></i>
              <p className="text-gray-600 dark:text-gray-400">Chargement des artisans...</p>
            </div>
          </div>
        ) : artisans.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-12">
              {artisans.map((artisan) => (
                <ArtisanCard key={artisan._id} artisan={artisan} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= filters.page - 2 && page <= filters.page + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 border rounded-lg ${
                          page === filters.page
                            ? 'bg-primary-800 dark:bg-primary-700 text-white border-primary-800 dark:border-primary-700'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }
                  if (page === filters.page - 3 || page === filters.page + 3) {
                    return <span key={page} className="px-2 text-gray-400 dark:text-gray-500">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === totalPages}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <i className="fas fa-search text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Aucun artisan trouvé</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Essayez de modifier vos critères de recherche
            </p>
            <button
              onClick={() => setFilters({
                page: 1,
                limit: 12,
                region: '',
                specialite: '',
                recherche: ''
              })}
              className="px-6 py-2 bg-primary-800 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              Voir tous les artisans
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Artisans;
